import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiRequest } from "../../utils/apiClient";
import { Order } from "../../types/Interface";
import { Carousel } from "react-responsive-carousel";
import ZoomableImage from "../quote/ZoomableImage";
import { useVendorContext } from "../../context/VendorContext";

const OrderDetailsPage = () => {
  const { id } = useParams();
  const [order, setOrder] = useState<Order>();
  const [loading, setLoading] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const { role } = useVendorContext();

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    const parsed = new Date(dateStr);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    const [year, month, day] = dateStr.split("-");
    if (!year || !month || !day) return dateStr;
    const fallback = new Date(+year, +month - 1, +day);
    return fallback.toLocaleDateString("en-GB").replace(/\//g, "-");
  };

  const orderStatusesCompany = [
    "PROCESSING", "COMPLETE", "SHIPPED", "DELIVERED", "CANCELLED", "RETURNED", "FAILED"
  ];

  const orderStatusesVendor = [
    "PROCESSING", "COMPLETE"
  ];

  const getStatusBadgeClass = (status?: string) => {
    const value = (status || "").toUpperCase();
    if (["DELIVERED", "COMPLETE"].includes(value)) return "bg-emerald-100 text-emerald-800";
    if (["CANCELLED", "FAILED"].includes(value)) return "bg-rose-100 text-rose-800";
    if (["SHIPPED", "OUT_FOR_DELIVERY"].includes(value)) return "bg-orange-100 text-orange-800";
    if (["PROCESSING", "CONFIRMED"].includes(value)) return "bg-blue-100 text-blue-800";
    return "bg-slate-100 text-slate-700";
  };

  const formatAmount = (value?: number) => `₹${Number(value || 0).toLocaleString("en-IN")}`;
  
  const handleStatusChange = async (newStatus: string) => {
    if (!order?.id) return;
    setStatusUpdating(true);
    try {
      await apiRequest(`/orders/${order.id}/status`, {
        method: "PUT",
        body: { status: newStatus },
        headers: { "Content-Type": "application/json" },
      });

      setOrder((prev) => prev ? { ...prev, status: newStatus } : prev);
    } catch (err) {
      console.error("Failed to update status", err);
    } finally {
      setStatusUpdating(false);
    }
  };

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await apiRequest(`/orders/id?id=${id}`);
        setOrder(response);
      } catch (error) {
        console.error("Failed to fetch order details", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  if (loading) return <div className="p-6 text-slate-600">Loading order details...</div>;
  if (!order) return <div className="p-6 text-rose-600">Order not found</div>;

  return (
    <div className="max-w-9xl mx-auto p-4 md:p-6 space-y-4">
      <button
        className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        onClick={() => window.history.back()}
      >
        ← Back to Orders
      </button>

      <div className="rounded-xl border border-slate-200 bg-gradient-to-r from-amber-50 via-white to-slate-50 p-4 md:p-5 shadow-sm">
        <h1 className="text-2xl font-bold text-slate-900">Order #{order.id}</h1>
        <p className="mt-1 text-sm text-slate-600">Placed on {formatDate(order.createdAt)}</p>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <p className="text-xs text-slate-500">Status</p>
          <span className={`mt-1 inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusBadgeClass(order.status)}`}>
            {order.status}
          </span>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <p className="text-xs text-slate-500">Payment</p>
          <span className={`mt-1 inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusBadgeClass(order.paymentStatus)}`}>
            {order.paymentStatus || "N/A"}
          </span>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <p className="text-xs text-slate-500">Items</p>
          <p className="text-lg font-semibold text-slate-900">{order.orderItems?.length || 0}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <p className="text-xs text-slate-500">Order Total</p>
          <p className="text-lg font-semibold text-slate-900">{formatAmount(order.orderTotal)}</p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900">Order Control</h2>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <label className="text-sm font-medium text-slate-700">Update Status</label>
          <select
            value={order.status}
            onChange={(e) => handleStatusChange(e.target.value)}
            disabled={statusUpdating}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none"
          >
            {role == "COMPANY" && orderStatusesCompany.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
            {role == "VENDOR" && orderStatusesVendor.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
          {statusUpdating && <span className="text-xs text-slate-500">Updating status...</span>}
        </div>
      </div>

      {role == "COMPANY" && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Customer Information</h2>
          <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-slate-700 md:grid-cols-2">
            <p><strong>Email:</strong> {order.user?.email || "N/A"}</p>
            <p><strong>Mobile:</strong> {order.user?.mobile || "N/A"}</p>
            <p><strong>Address:</strong> {order.user?.billingAddress?.billingAddress || "N/A"}</p>
            <p><strong>State:</strong> {order.user?.billingAddress?.billingState || "N/A"}</p>
            <p><strong>City:</strong> {order.user?.billingAddress?.billingCity || "N/A"}</p>
            <p><strong>Country:</strong> {order.user?.billingAddress?.billingCountry || "N/A"}</p>
            <p><strong>Pincode:</strong> {order.user?.billingAddress?.billingPostalCode || "N/A"}</p>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900">Order Items</h2>
        <div className="mt-4 space-y-4">
          {Array.isArray(order.orderItems) && order.orderItems.map((item, index) => (
            <div key={index} className="rounded-lg border border-slate-200 bg-slate-50 p-3 md:p-4">
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                <div>
                  <div className="bg-white p-2 rounded-lg shadow-sm max-w-[240px]">
                    <div className="mb-1 font-semibold text-slate-900">{item.product?.productName || "N/A"}</div>
                    <div className="text-xs text-slate-500 mb-2">Code: {item.product?.productCode || "N/A"}</div>
                    {item.product?.image && item.product.image.length > 0 && (
                      <Carousel
                        showThumbs={false}
                        showIndicators={true}
                        showStatus={false}
                        infiniteLoop
                        className="w-52 rounded-md bg-white"
                      >
                        {item.product.image.map((img, imageIndex) => (
                          <div key={imageIndex} className="h-52 w-52 flex justify-center items-center bg-gray-100">
                            <ZoomableImage src={img} alt={`Product image ${imageIndex + 1}`} />
                          </div>
                        ))}
                      </Carousel>
                    )}
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                    <p><strong>Quantity:</strong> {item.quantity}</p>
                    <p><strong>Line Total:</strong> {formatAmount(Number(item.price || 0) * Number(item.quantity || 0))}</p>
                  </div>
                </div>

                <div className="rounded-md border border-slate-200 bg-white p-3 text-sm">
                  <h3 className="font-semibold text-slate-800 mb-2">Price Breakup</h3>
                  <div className="space-y-1 text-slate-700">
                    <div><strong>Actual Diamond Price:</strong> {item.product?.priceBreakup?.actualDiamondPrice ?? "N/A"}/-</div>
                    <div><strong>Discounted Diamond Price:</strong> {item.product?.priceBreakup?.discountedDiamondPrice ?? "N/A"}/-</div>
                    <div><strong>Metal Price:</strong> {item.product?.priceBreakup?.metal ?? "N/A"}/-</div>
                    <div><strong>Making Charges:</strong> {item.product?.priceBreakup?.makingCharges ?? "N/A"}/-</div>
                    <div><strong>Discounted Making Charges:</strong> {item.product?.priceBreakup?.discountMakingCharges ?? "N/A"}/-</div>
                    <div><strong>GST:</strong> {item.product?.priceBreakup?.gst ?? "N/A"}/-</div>
                    <div><strong>Total:</strong> {item.product?.priceBreakup?.total ?? "N/A"}/-</div>
                    <div><strong>Discounted Total:</strong> {item.product?.priceBreakup?.discountTotal ?? "N/A"}/-</div>
                  </div>
                </div>

                <div className="rounded-md border border-slate-200 bg-white p-3 text-sm">
                  <h3 className="font-semibold text-slate-800 mb-2">Customization</h3>
                  <div className="space-y-1 text-slate-700">
                    <div><strong>Purity:</strong> {item.customizationInfo?.purity ?? "N/A"}</div>
                    <div><strong>Diamond Color:</strong> {item.customizationInfo?.diamondColor ?? "N/A"}</div>
                    <div><strong>Diamond Clarity:</strong> {item.customizationInfo?.diamondClarity ?? "N/A"}</div>
                    <div><strong>Solitaire Weight:</strong> {item.customizationInfo?.stWt ?? "N/A"}</div>
                    <div><strong>Metal Color:</strong> {item.customizationInfo?.metalColor ?? "N/A"}</div>
                    <div><strong>Ring Size:</strong> {item.customizationInfo?.size ?? "N/A"}</div>
                    <div><strong>Engraving:</strong> {item.customizationInfo?.engraving ?? "N/A"}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsPage;
